<i18n>
{
  "cl": {
    "-": "-",
    "refused": "Rechazado",
    "approved": "Aprobado",
    "review": "Revisión",
    "with offer": "Propuesta",
    "without offer": "Sin oferta",
    "eligible": "Cumple",
    "not eligible": "No cumple"
  }
}
</i18n>
<template>
  <div>
    <b-table
      :data="data"
      class="evaluation-table"
      :draggable="false"
      show-detail-icon>
      <b-table-column v-slot="{ row }" field="index" label="" class="featured-column">
        <router-link
          :to="getEvaluationResumeLink(row)"
          :data-cy="`open-resume-${row.index}`"
          class="has-text-primary">
          Evaluación {{ row.index }}
        </router-link>
      </b-table-column>
      <b-table-column v-slot="{ row }" field="createdAt" label="Fecha">
        {{ formatDate(row.createdAt) }}
      </b-table-column>
      <b-table-column v-slot="{ row }" field="creditAmount" label="Solicitado">
        {{ formatNumberByCountry(row.creditAmount) }}
      </b-table-column>
      <b-table-column v-slot="{ row }" field="approvedCreditAmount" label="Aprobado">
        {{ formatNumberByCountry(row.approvedCreditAmount) }}
      </b-table-column>
      <b-table-column v-slot="{ row }" field="financingRatio" label="Resultado">
        <b-tag
          :type="getResult(row.financingRatio, row.approvedCreditAmount).type"
          :data-cy="`result-${row.index}`"
          class="result-icon-list is-light fixed-width-tag offer-result"
          rounded>
          {{ $t(getResult(row.financingRatio, row.approvedCreditAmount).label) }}
        </b-tag>
      </b-table-column>
      <b-table-column v-slot="{ row }" field="result" label="Elegibilidad">
        <b-tag
          :type="getEvaluationResult(row.eligibility).type"
          :data-cy="`eligibility-${row.index}`"
          class="result-icon-list is-light fixed-width-tag eligibility-result"
          rounded>
          {{ $t(getEvaluationResult(row.eligibility).label) }}
        </b-tag>
      </b-table-column>
      <b-table-column v-slot="{ row }" field="future" label="Tipo">
        {{ getType(row.future) }}
      </b-table-column>
      <b-table-column v-slot="{ row }" field="createdBy" label="Evaluador">
        <evaluator-avatar
          :evaluator-id="row.createdBy?.userId"
          :data-cy="row.createdBy?.userId ? 'manual-evaluation':'auto-evaluation'" />
      </b-table-column>
      <b-table-column
        v-slot="{ row }"
        field="evidence"
        label="Reporte">
        <b-button
          v-if="showRatify(row)"
          class="mr-2 mb-1"
          size="is-small"
          type="is-danger"
          icon-left="check-double"
          rounded
          outlined
          :data-cy="`ratify-evaluation-${row.index}`"
          @click="goResume(row)">
          Ratificar
        </b-button>
        <b-button
          v-if="row.evidence && row.status === 'RATIFIED'"
          size="is-small"
          icon-left="download"
          tag="a"
          outlined
          :href="row.evidence"
          :data-cy="`download-button-${row.index}`"
          download="verificacion.pdf"
          class="download-button"
          rounded>
          Descargar
        </b-button>
      </b-table-column>
    </b-table>
  </div>
</template>

<script>
import { BffEvaluationsClient } from '@/application/services/evaluations/apis/bff-evaluations-client';
import { formatDate, formatNumberByCountry } from '@/infrastructure/format';
import EvaluatorAvatar from '@/ui/components/internal/evaluations/EvaluatorAvatar.vue';
import {
  getEvaluationResult,
  getResult
} from '@/application/services/evaluations/evaluation-result-service';
import locale from '@/ui/mixins/locale';
import { EvaluationRetriever } from '@/application/services/evaluations/evaluation-retriever';
import { evaluationsBackend } from '@/application/api';
import { authenticationService } from '@/application/services/api-clients/login-service';

export default {
  name: 'EvaluationTable',
  components: {
    EvaluatorAvatar
  },
  mixins: [locale],
  props: {
    idNumber: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      data: [],
      evidence: [],
      selected: {},
      userInfoResponse: {}
    };
  },
  async mounted() {
    await this.fetchEvaluationData();
  },
  methods: {
    formatDate,
    getResult,
    getType(type) {
      return type ? 'Futura' : 'Inmediata';
    },
    getEvaluationResumeLink({ evaluationId, type }) {
      return ({
        name: 'evaluation-summary',
        params: {
          evaluationId,
          evaluationType: type || 'automatic'
        }
      });
    },
    async fetchEvaluationData() {
      try {
        const evaluationsClient = new EvaluationRetriever(new BffEvaluationsClient(evaluationsBackend, authenticationService));
        const evaluationList = await evaluationsClient.getEvaluations(this.idNumber);
        this.data = await evaluationList.reduce(async (acc, evaluation) => {
          const fullEvaluation = evaluation;
          const evaluations = await acc;
          const hasEvidence = await this.getEvaluationEvidence(evaluation);
          fullEvaluation.evidence = hasEvidence ? `data:application/pdf;base64,${hasEvidence}` : null;
          evaluations.push(fullEvaluation);
          return evaluations;
        }, []);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error.message);
      }
    },
    formatNumberByCountry(number) {
      if (number === null || number === undefined) {
        return 'UF 0';
      }
      return formatNumberByCountry({
        number,
        countryIn: this.locale(),
        usePrefix: true,
        currencyType: 'primary'
      });
    },
    getEvaluationResult,
    goResume({ evaluationId }) {
      this.$router.push({
        name: 'evaluation-summary',
        params: {
          evaluationId,
          evaluationType: 'automatic'
        }
      });
    },
    showRatify({ result, type, status }) {
      return type === 'automatic' && status !== 'RATIFIED' && result === 'Approved';
    },
    async getEvaluationEvidence({ evaluationId }) {
      try {
        const evaluationsClient = new EvaluationRetriever(new BffEvaluationsClient(evaluationsBackend, authenticationService));
        return await evaluationsClient.getEvidence(evaluationId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error.message);
      }
      return null;
    }
  }
};

</script>

<style>
.evaluation-table .table {
  border: 1px solid #e5e5e5 !important;
  font-size: 14px;
  border-radius: 10px;
  border-collapse: separate;
  overflow: hidden;
  margin-bottom: 1rem;
}

.evaluation-table .table th {
  background-color: #f4f4f4;
  border: none;
}

.evaluation-table .table tr.detail {
  background-color: #FFFFFF;
  box-shadow: none;
}

.evaluation-table .table tr td,
.evaluation-table .table tr.detail td {
  border: none;
}

.evaluation-table .table tr td {
  border-top: 1px solid #e5e5e5;
  vertical-align: middle;
}

.evaluation-table .table a {
  color: var(--primary);
}

.evaluation-table .fa-ul {
  margin-left: 21px;
}

.evaluation-table ul.logs {
  list-style-type: disc;
  padding-left: 20px;
  color: #333333;
  font-size: 14px;
}

.evaluation-icon-user i {
  color: #828282;
}

.evaluation-table .tag.fixed-width-tag {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 80px;
  text-align: center;
}

@media only screen and (max-width: 768px) {
  .evaluation-table .b-table .table {
    border: none;
  }

  .evaluation-table .b-table .table thead {
    display: none !important;
  }

  .evaluation-table .b-table .table tr {
    box-shadow: none !important;
    border: 1px solid #dbdbdb !important;
    border-radius: 10px;
  }

  .evaluation-table .b-table .table-wrapper.has-mobile-cards tr:not(.detail):not(.is-empty):not(.table-footer) td {
    border: none;
  }
}
</style>
